// S-8 `/consent` — 3 обязательных согласия перед медицинской анкетой.
// Source: docs/specs/sprint-3/stage-4-screens.md §5.8.

import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'wouter';
import type { LucideIcon } from 'lucide-react';
import { Globe2, ScrollText, ShieldCheck } from 'lucide-react';
import { ConsentSchema, type ConsentForm } from '@/api/schemas';
import { IconTile } from '@/components/ui/icon-tile';
import { usePostConsent } from '@/api/hooks/consent';
import { useProfileMe } from '@/api/hooks/profile';
import { applyValidationErrors, useApiErrorToast } from '@/api/error';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { LinkText } from '@/components/layout/LinkText';
import { LegalSheet } from '@/components/legal/LegalSheet';
import type { LegalKind } from '@/content/legal';
import { SubmitButton } from '@/components/forms/SubmitButton';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type ConsentItem = {
  id: keyof ConsentForm;
  icon: LucideIcon;
  title: string;
  text: string;
};

const consentItems: ConsentItem[] = [
  {
    id: 'pd_medical',
    icon: ShieldCheck,
    title: 'Персональные и медицинские данные',
    text: 'Согласен(на) на обработку персональных данных и данных о здоровье для создания профиля, хранения анкеты, результатов анализов и работы сервиса HealthLab.',
  },
  {
    id: 'cross_border_openai',
    icon: Globe2,
    title: 'AI-обработка и трансграничная передача',
    text: 'Согласен(на), что часть данных может передаваться в сервисы OpenAI/США для AI-обработки и формирования персональных подсказок.',
  },
  {
    id: 'tos_privacy',
    icon: ScrollText,
    title: 'Условия и политика конфиденциальности',
    text: 'Принимаю Условия использования и Политику конфиденциальности HealthLab, включая описание сервисной аналитики.',
  },
];

const serverFieldMap: Record<string, keyof ConsentForm> = {
  'flags.pd_medical': 'pd_medical',
  'flags.cross_border_openai': 'cross_border_openai',
  'flags.tos_privacy': 'tos_privacy',
};

export function Consent() {
  const [, setLocation] = useLocation();
  const profileQuery = useProfileMe();
  const postConsentMutation = usePostConsent();
  const showErrorToast = useApiErrorToast();
  const { toast } = useToast();

  const consentVersion = profileQuery.data?.meta.current_consent_version;
  const [openDoc, setOpenDoc] = useState<LegalKind | null>(null);

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<ConsentForm>({
    resolver: zodResolver(ConsentSchema),
    defaultValues: {
      pd_medical: false,
      cross_border_openai: false,
      tos_privacy: false,
    },
  });

  const values = watch();
  const allChecked = values.pd_medical && values.cross_border_openai && values.tos_privacy;

  const onSubmit = handleSubmit(async (flags) => {
    if (!consentVersion) {
      toast({
        title: 'Версия согласия не загружена',
        description: 'Обновите страницу и попробуйте ещё раз',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await postConsentMutation.mutateAsync({
        consent_version: consentVersion,
        flags,
      });

      if (response.activated) {
        setLocation('/onboarding/intro');
        return;
      }

      toast({
        title: 'Согласия не активированы',
        description: response.warning ?? 'Для продолжения нужны все три согласия.',
        variant: 'destructive',
      });
    } catch (err) {
      const applied = applyValidationErrors<keyof ConsentForm>(err, setError, serverFieldMap);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <ScreenHeader title="Согласия" />

      <main className="flex-1 overflow-y-auto px-6 pb-8 pt-2">
        <div className="mb-5">
          <IconTile size="md" className="mb-3">
            <ShieldCheck className="h-6 w-6" />
          </IconTile>
          <h1 className="mb-2 text-[24px] font-bold leading-tight">Перед началом анкеты</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Нам нужно ваше согласие на обработку данных. Без него мы не можем открыть медицинскую анкету.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3" noValidate>
          {consentItems.map((item) => {
            const Icon = item.icon;
            const error = errors[item.id]?.message;

            return (
              <Controller
                key={item.id}
                control={control}
                name={item.id}
                render={({ field }) => (
                  <label
                    htmlFor={item.id}
                    className={cn(
                      'flex cursor-pointer gap-3 rounded-control border bg-card p-4 transition-colors',
                      field.value ? 'border-primary/60 bg-primary/5' : 'border-border',
                      error ? 'border-destructive/60' : '',
                    )}
                  >
                    <Checkbox
                      id={item.id}
                      checked={field.value}
                      onBlur={field.onBlur}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      className="mt-1"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="mb-1 flex items-center gap-2 text-sm font-semibold">
                        <Icon className="h-4 w-4 text-primary-text" />
                        {item.title}
                      </span>
                      <span className="block text-hl-small leading-relaxed text-muted-foreground">
                        {item.text}
                      </span>
                      {error && (
                        <span className="mt-2 block text-hl-small font-medium text-destructive">
                          {error}
                        </span>
                      )}
                    </span>
                  </label>
                )}
              />
            );
          })}

          <div className="rounded-control border border-blue-200 bg-blue-50 p-3 text-hl-small leading-snug text-blue-900">
            Актуальная версия:{' '}
            {consentVersion ? <span className="font-mono">{consentVersion}</span> : 'загрузка'}.
            {/* Документ открывается листом поверх экрана, а не target="_blank" и не уходом
                на /legal/*. «Новое окно» в нативной сборке отдаёт наружу внутренний адрес
                capacitor://localhost, который системный браузер не понимает — документ не
                открывался вовсе. Уход на /legal/* работал, но размонтировал форму и сбрасывал
                уже проставленные галочки (HEAL-69). Лист сохраняет и то, и другое. */}
            Документы:{' '}
            <LinkText onClick={() => setOpenDoc('privacy')}>Политика конфиденциальности</LinkText>
            {' и '}
            <LinkText onClick={() => setOpenDoc('tos')}>Условия использования</LinkText>.
          </div>

          <SubmitButton
            isPending={postConsentMutation.isPending}
            disabled={!consentVersion || !allChecked || profileQuery.isFetching}
            className="mt-2"
          >
            Подтвердить и продолжить
          </SubmitButton>
        </form>

        <LegalSheet kind={openDoc} onClose={() => setOpenDoc(null)} />
      </main>
    </MobileFrame>
  );
}
